import { FlexBox, HelpIcon, Text } from '@dixa/lib-shared-habla';
import type {
  RoutedThroughChatbotToolCalledEntry,
  ToolType,
} from 'lib-shared-legacy-types';
import { useMemo } from 'react';

import { filesApi } from 'apis/files';
import { CodeHighlight } from 'components/codeHighlight';

import { LogEntry } from '../logEntry';

import { AutomationTaskExecutionActivity } from './automationExecutionActivity';

type Props = {
  entry: RoutedThroughChatbotToolCalledEntry;
};
const toolTypeLabel: Record<ToolType, string> = {
  automation_task: 'Task',
  automation_handoff: 'Handoff',
  retrieval: 'Knowledge',
  procedure: 'Procedure',
  static: 'Static',
  integration: 'Integration',
};

export const RoutedThroughChatbotToolCalled = ({
  entry,
}: Props): JSX.Element | null => {
  const { csid, tool_name, tool_type, input, output, execution_data, parent } =
    entry;

  const {
    content: outputContent,
    isLoading,
    isError,
  } = filesApi.useReadFileQuery(
    { path: output?.file?.s3_path || '' },
    {
      skip: !output?.file?.s3_path,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        content: data?.data?.content,
      }),
    },
  );

  const outputResult = useMemo(() => {
    if (isError) {
      return (
        <FlexBox as="div" padding="0 300">
          <Text.Paragraph variant="3" subdued>
            Error loading output
          </Text.Paragraph>
        </FlexBox>
      );
    }
    if (isLoading || !outputContent) {
      return (
        <FlexBox as="div" padding="0 300">
          <Text.Paragraph variant="3" subdued>
            Loading output...
          </Text.Paragraph>
        </FlexBox>
      );
    }
    return (
      <CodeHighlight
        languages={['json']}
        style={{
          padding: '100 100',
          overflow: 'scroll',
          maxHeight: '500px',
        }}
      >
        {typeof outputContent === 'string'
          ? outputContent
          : JSON.stringify(outputContent, null, 2)}
      </CodeHighlight>
    );
  }, [isError, isLoading, outputContent]);
  if (
    tool_type === 'static' &&
    tool_name === 'think' &&
    output?.file?.s3_path &&
    outputResult
  ) {
    return (
      <LogEntry
        entry={entry}
        icon="wrench"
        text={`AI agent thinking`}
        isExpandable
      >
        <FlexBox margin="200 0 0 500" gap="300" flexDirection="column">
          <FlexBox gap="200" flexDirection="column">
            {outputResult}
          </FlexBox>
          {execution_data?.automation_tool_call_execution_data && (
            <AutomationTaskExecutionActivity
              csid={csid}
              executionData={execution_data.automation_tool_call_execution_data}
            ></AutomationTaskExecutionActivity>
          )}
        </FlexBox>
      </LogEntry>
    );
  }
  if (tool_type === 'static') {
    return null;
  }

  const toolCallText =
    tool_type == 'procedure'
      ? `${toolTypeLabel[tool_type]}: ${tool_name}`
      : `${toolTypeLabel[tool_type]} tool call: ${tool_name}`;

  const secondaryText = parent ? `Procedure: ${parent.tool_name}` : null;
  return (
    <LogEntry
      entry={entry}
      icon="wrench"
      text={toolCallText}
      secondaryText={secondaryText}
      isExpandable
    >
      <FlexBox margin="200 0 0 500" gap="300" flexDirection="column">
        <FlexBox gap="200" flexDirection="column">
          <FlexBox flexDirection="row" gap="100" alignItems="center">
            <Text.Paragraph variant="3"> Input</Text.Paragraph>
            <HelpIcon
              text={
                'The input is derived from conversation or knowledge. Refine its description to yield more accurate results.'
              }
              side="right"
            ></HelpIcon>
          </FlexBox>
          <CodeHighlight
            languages={['json']}
            style={{
              padding: '100 100',
              overflow: 'scroll',
              maxHeight: '500px',
            }}
          >
            {JSON.stringify(input.parameters ?? {}, null, 2)}
          </CodeHighlight>
        </FlexBox>

        <FlexBox gap="200" flexDirection="column">
          <FlexBox flexDirection="row" gap="100" alignItems="center">
            <Text.Paragraph variant="3"> Output</Text.Paragraph>
            <HelpIcon
              text={'The output used to compose the next response.'}
              side="right"
            ></HelpIcon>
          </FlexBox>
          {output?.file?.s3_path ? (
            outputResult
          ) : (
            <FlexBox as="div" padding="0 300">
              <Text.Paragraph variant="3" subdued>
                Not used
              </Text.Paragraph>
            </FlexBox>
          )}
        </FlexBox>

        {execution_data?.automation_tool_call_execution_data && (
          <AutomationTaskExecutionActivity
            csid={csid}
            executionData={execution_data.automation_tool_call_execution_data}
          ></AutomationTaskExecutionActivity>
        )}
      </FlexBox>
    </LogEntry>
  );
};
